import { isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";
import type { Diagnosis } from "@/src/models/documents/referral";

export interface ConfluentCaseNameInput {
  diagnosisList?: Diagnosis[] | null;
  reasonForReferral?: string | null;
  bodyPart?: string | null;
  primaryEhrPayerName?: string | null;
  serviceLine?: string | null;
  dateOfBirth?: string | null;
  isPostOpReferral?: boolean | null;
  isPostOpPriorAuth?: boolean | null;
}

interface KeywordGroup {
  label: string;
  synonyms: string[];
}

const DEFAULT_CASE_NAME = "Default Case";

const PELVIC_HEALTH_KEYWORDS = [
  "Coccyx",
  "Coccyxdynia",
  "Coccydynia",
  "Constipation",
  "Dysparenunia",
  "Dyspareunia",
  "Endometriosis",
  "Erectile Dysfunction",
  "Frequency of Micturition",
  "Hard flaccid",
  "Hydrocele",
  "IBS",
  "Induratio Penis plastica",
  "Peyronie's Disease",
  "Interstitial Cystitis",
  "Levator Ani Syndrome",
  "Mixed incontinence",
  "Overactive Bladder",
  "Overflow Incontinence",
  "Overflow Urinary Incontinence",
  "Pelvic Pain",
  "Post partum",
  "Postpartum",
  "Pre Prostatectomy",
  "Post prostatectomy",
  "Prostatectomy",
  "Post void Dribbling",
  "Pregnancy",
  "Premature Ejaculation",
  "Prolapse",
  "Cystocele",
  "Rectocele",
  "Uterine Prolapse",
  "Vaginal Vault Prolapse",
  "Prostatitis",
  "Pudendal Neuralgia",
  "Stress Incontinence",
  "Urge Incontinence",
  "Urinary Retention",
  "Vaginismus",
  "Varicocele",
  "Vestibulodynia",
];

const VESTIBULAR_KEYWORD_GROUPS: KeywordGroup[] = [
  { label: "Vertigo", synonyms: ["Vertigo"] },
  { label: "Dizziness", synonyms: ["Dizziness", "Dizzy"] },
  { label: "BPPV", synonyms: ["BPPV"] },
  { label: "Vestibular Dysfunction", synonyms: ["Vestibular Dysfunction"] },
  { label: "Vestibular Disorder", synonyms: ["Vestibular Disorder"] },
  { label: "Vestibulopathy", synonyms: ["Vestibulopathy"] },
  { label: "Vestibular Hypofunction", synonyms: ["Vestibular Hypofunction"] },
  { label: "Vestibular Neuritis", synonyms: ["Vestibular Neuritis"] },
  { label: "Labyrinthitis", synonyms: ["Labyrinthitis"] },
  { label: "Meniere's Disease", synonyms: ["Meniere's Disease", "Menieres Disease"] },
  { label: "Vestibular Migraine", synonyms: ["Vestibular Migraine"] },
  { label: "Cervicogenic Dizziness", synonyms: ["Cervicogenic Dizziness"] },
  { label: "Disequilibrium", synonyms: ["Disequilibrium"] },
  { label: "Motion Sensitivity", synonyms: ["Motion Sensitivity"] },
  { label: "Motion-induced Dizziness", synonyms: ["Motion-induced Dizziness"] },
  { label: "Positional Dizziness", synonyms: ["Positional Dizziness"] },
  { label: "Positional Vertigo", synonyms: ["Positional Vertigo"] },
];

const NEURO_KEYWORD_GROUPS: KeywordGroup[] = [
  { label: "CVA", synonyms: ["CVA", "Cerebrovascular Accident"] },
  { label: "TIA", synonyms: ["TIA"] },
  { label: "Hemiplegia", synonyms: ["Hemiplegia"] },
  { label: "Hemiparesis", synonyms: ["Hemiparesis"] },
  { label: "MS", synonyms: ["Multiple Sclerosis", "MS"] },
  { label: "PD", synonyms: ["Parkinson's Disease", "Parkinson's", "Parkinsons", "PD"] },
  { label: "TBI", synonyms: ["Traumatic Brain Injury", "Brain Injury", "TBI"] },
  { label: "ABI", synonyms: ["Acquired Brain Injury", "ABI"] },
  { label: "Incomplete SCI", synonyms: ["Incomplete Spinal Cord Injury"] },
  { label: "SCI", synonyms: ["Spinal Cord Injury", "SCI"] },
  { label: "CP", synonyms: ["Cerebral Palsy", "CP"] },
  { label: "Spina Bifida", synonyms: ["Spina Bifida"] },
  { label: "Muscular Dystrophy", synonyms: ["Muscular Dystrophy"] },
  { label: "ALS", synonyms: ["ALS", "Amyotrophic Lateral Sclerosis"] },
  { label: "GBS", synonyms: ["GBS", "Guillain Barre"] },
  { label: "Transverse Myelitis", synonyms: ["Transverse Myelitis"] },
  { label: "Peripheral Neuropathy", synonyms: ["Peripheral Neuropathy"] },
  { label: "Polyneuropathy", synonyms: ["Polyneuropathy"] },
  { label: "Neuropathy", synonyms: ["Neuropathy"] },
  { label: "Myelopathy", synonyms: ["Myelopathy"] },
  { label: "Quadriplegia", synonyms: ["Quadriplegia"] },
  { label: "Tetraplegia", synonyms: ["Tetraplegia"] },
  { label: "Paraplegia", synonyms: ["Paraplegia"] },
  { label: "Huntington's Disease", synonyms: ["Huntington's Disease", "Huntingtons Disease"] },
  { label: "Friedreich Ataxia", synonyms: ["Friedreich Ataxia"] },
  { label: "Ataxia", synonyms: ["Ataxia"] },
];

const LATERALITY_ABBREVIATIONS: Record<string, string> = {
  left: "L",
  right: "R",
  bilateral: "Bilat",
};

const NEGATION_TRIGGERS = [
  "without",
  "w/o",
  "no",
  "not",
  "negative for",
  "absence of",
  "free of",
  "denies",
  "denied",
  "sans",
];
const NEGATION_SCOPE_TERMINATORS = ["with", "but", "except", "however", "positive for"];

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const NEGATION_TRIGGER_PATTERN = new RegExp(`\\b(?:${NEGATION_TRIGGERS.map(escapeForRegExp).join("|")})\\b`, "gi");
const NEGATION_TERMINATOR_PATTERN = new RegExp(
  `\\b(?:${NEGATION_SCOPE_TERMINATORS.map(escapeForRegExp).join("|")})\\b`,
  "i"
);
const PSEUDO_NEGATION_TAIL_PATTERN = new RegExp(
  "^\\s*(?:significant|noticeable|notable|interval|much|any|further|new)?\\s*" +
    "(?:improvement|improving|improved|change|changes|relief|relieved|resolution|resolved|resolving|" +
    "responding|response|worsening|worse|better)\\b",
  "i"
);

function findRealNegation(clause: string): { index: number; length: number } | null {
  NEGATION_TRIGGER_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;

  while ((match = NEGATION_TRIGGER_PATTERN.exec(clause)) !== null) {
    const afterTrigger = clause.slice(match.index + match[0].length);

    if (!PSEUDO_NEGATION_TAIL_PATTERN.test(afterTrigger)) {
      return { index: match.index, length: match[0].length };
    }
  }

  return null;
}

function stripNegatedTerms(text: string): string {
  return text
    .split(/[,;.\n]+/)
    .map((clause) => {
      const negation = findRealNegation(clause);

      if (!negation) return clause;

      const beforeNegation = clause.slice(0, negation.index);
      const afterTrigger = clause.slice(negation.index + negation.length);
      const terminator = NEGATION_TERMINATOR_PATTERN.exec(afterTrigger);
      const positiveTail = terminator ? stripNegatedTerms(afterTrigger.slice(terminator.index)) : "";

      return `${beforeNegation} ${positiveTail}`;
    })
    .join(" ");
}

function normalizeForMatch(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function isNotBlank(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function diagnosisListIsBlank(diagnosisList: Diagnosis[] | null | undefined): boolean {
  if (!diagnosisList || diagnosisList.length === 0) {
    return true;
  }

  return diagnosisList.every((d) => !isNotBlank(d.diagnosisText) && !isNotBlank(d.diagnosisCode));
}

function buildKeywordHaystack(input: ConfluentCaseNameInput): string {
  const diagnosisText = (input.diagnosisList ?? [])
    .map((d) => stripNegatedTerms(d.diagnosisText ?? ""))
    .filter((t) => t.trim().length > 0)
    .join(" ");
  const reasonForReferral = stripNegatedTerms(input.reasonForReferral ?? "");
  const combined = [diagnosisText, reasonForReferral, input.bodyPart ?? ""].join(" ");

  return ` ${normalizeForMatch(combined)} `;
}

function matchesAnyKeyword(paddedHaystack: string, keywords: string[]): boolean {
  return keywords.some((keyword) => paddedHaystack.includes(` ${normalizeForMatch(keyword)} `));
}

function matchKeywordGroupLabel(paddedHaystack: string, groups: KeywordGroup[]): string | null {
  const candidates = groups
    .flatMap((group) =>
      group.synonyms.map((synonym) => ({ label: group.label, needle: ` ${normalizeForMatch(synonym)} ` }))
    )
    .sort((a, b) => b.needle.length - a.needle.length);

  for (const candidate of candidates) {
    if (paddedHaystack.includes(candidate.needle)) {
      return candidate.label;
    }
  }

  return null;
}

function getServiceLinePrefix(serviceLine: string | null | undefined): string | null {
  if (!isNotBlank(serviceLine)) {
    return null;
  }

  const normalized = normalizeForMatch(serviceLine);

  if (normalized.includes("aquatic")) {
    return "Aquatic";
  }

  if (normalized.includes("occupational")) {
    return "OT";
  }

  if (normalized.includes("speech")) {
    return "Speech";
  }

  return null;
}

function formatBodyPart(bodyPart: string | null | undefined): string {
  if (!isNotBlank(bodyPart)) {
    return "";
  }

  const words = bodyPart
    .trim()
    .split(/\s+/)
    .map((word) => {
      const key = word.toLowerCase().replace(/[^a-z]/g, "");

      if (key in LATERALITY_ABBREVIATIONS) {
        return LATERALITY_ABBREVIATIONS[key];
      }

      return word.length <= 2 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

  return words.slice(0, 3).join(" ");
}

function resolveCoreName(input: ConfluentCaseNameInput, paddedHaystack: string): string {
  if (matchesAnyKeyword(paddedHaystack, PELVIC_HEALTH_KEYWORDS)) {
    return "Pelvic Health";
  }

  const vestibular = matchKeywordGroupLabel(paddedHaystack, VESTIBULAR_KEYWORD_GROUPS);

  if (vestibular) {
    return `Vestibular-${vestibular}`;
  }

  const neuro = matchKeywordGroupLabel(paddedHaystack, NEURO_KEYWORD_GROUPS);

  if (neuro) {
    return `Neuro-${neuro}`;
  }

  const isPostOp = input.isPostOpReferral === true || input.isPostOpPriorAuth === true;
  const bodyPart = formatBodyPart(input.bodyPart);

  return [isPostOp ? "PO" : "", bodyPart].filter((part) => part.length > 0).join(" ");
}

export function buildConfluentAllStarCaseName(input: ConfluentCaseNameInput): string {
  if (
    diagnosisListIsBlank(input.diagnosisList) &&
    !isNotBlank(input.reasonForReferral) &&
    !isNotBlank(input.bodyPart)
  ) {
    return DEFAULT_CASE_NAME;
  }

  const paddedHaystack = buildKeywordHaystack(input);
  const core = resolveCoreName(input, paddedHaystack);

  const pediatricPrefix = isMinorDateOfBirth(input.dateOfBirth) ? "Pediatric" : null;
  const regalPrefix =
    isNotBlank(input.primaryEhrPayerName) && normalizeForMatch(input.primaryEhrPayerName).includes("regal")
      ? "Regal"
      : null;
  const serviceLinePrefix = getServiceLinePrefix(input.serviceLine);

  const caseName = [pediatricPrefix, regalPrefix, serviceLinePrefix, core]
    .filter((part): part is string => !!part && part.length > 0)
    .join(" ")
    .trim();

  return caseName.length > 0 ? caseName : DEFAULT_CASE_NAME;
}
